import { Language, LANGUAGE_STORAGE_KEY } from "@/components/Inputs/LanguagePicker/Language.ts";
import { URLParam } from "@/helpers/interfaces/URLParam.ts";
import i18n from "i18next";
import { useEffect } from "react";

export const useLanguageURLParams = () => {
  // Applies the language AND persists it, so the choice survives a reload that
  // drops the `lang` URL param — e.g. the WSPay cancel/error return URL (SW-8658).
  const changeLanguage = (lng: Language) => {
    void i18n.changeLanguage(lng);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, lng);
  };

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const languageParam = urlParams.get(URLParam.Language);
    const storedLanguage = localStorage.getItem(LANGUAGE_STORAGE_KEY);

    // Fallback order: URL param -> persisted language -> host/page default
    let language = languageParam || storedLanguage || getInitLanguage();

    if (language === "it-it" || language === "it-IT") language = Language.IT;
    if (language === "de-de" || language === "de-DE") language = Language.DE;
    if (language === "sl-si" || language === "sl-SI") language = Language.SL;
    if (language === "sr-rs" || language === "sr-RS") language = Language.SR;
    if (language === "cs-cz" || language === "cs-CZ") language = Language.CS;

    const isLanguageSupported = Object.values(Language).includes(language as Language);

    // Unsupported value: apply the page default, but never persist junk.
    if (!isLanguageSupported) {
      void i18n.changeLanguage(getInitLanguage());
      return;
    }

    // Supported: apply if needed, and always (re)persist so a later param-less
    // reload can recover the selected language.
    if ((i18n?.language as Language) !== (language as Language)) {
      changeLanguage(language as Language);
    } else {
      localStorage.setItem(LANGUAGE_STORAGE_KEY, language);
    }
  }, []);
};

export const getInitLanguage = (): Language => {
  let htmlElLanguage = document.documentElement?.lang;

  if (htmlElLanguage === "en-US" || htmlElLanguage === "en-GB") {
    htmlElLanguage = Language.EN;
  }

  if (htmlElLanguage === "it-it" || htmlElLanguage === "it-IT") htmlElLanguage = Language.IT;
  if (htmlElLanguage === "de-de" || htmlElLanguage === "de-DE") htmlElLanguage = Language.DE;
  if (htmlElLanguage === "sl-si" || htmlElLanguage === "sl-SI") htmlElLanguage = Language.SL;
  if (htmlElLanguage === "sr-rs" || htmlElLanguage === "sr-RS") htmlElLanguage = Language.SR;
  if (htmlElLanguage === "cs-cz" || htmlElLanguage === "cs-CZ") htmlElLanguage = Language.CS;

  return (htmlElLanguage as Language) || Language.EN;
};
